import { faCircle, faRedo } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { DashboardLayout } from '../../../../components/DashboardLayout/DashboardLayout'
import { ErrorContent } from '../../../../components/ErrorContent/ErrorContent'
import { Header } from '../../../../components/Header/Header'
import { Title } from '../../../../components/Title/Title'
import { ROUTES } from '../../../../constants/routes'
import { useRouter } from '../../../../hooks/useRouter'
import { LoadingWhatsNewDetails } from './components/LoadingWhatsNewDetails'
import { useFetchConfluenceContent } from './hooks/useFetchConfluenceContent'
import { TruncatedText } from '../../../../components/TruncatedText/TruncatedText'

export const WhatsNewDetails = (): React.ReactElement => {
  const router = useRouter()
  const { id } = router.params
  const {
    data: whatsNewPage,
    error: errorList,
    isLoading: isLoadingList,
    isError: isErrorList,
    lastViewedOn,
  } = useFetchConfluenceContent({
    contentId: '131096',
  })
  const {
    data: currentPage,
    isLoading,
    isError,
    error,
  } = useFetchConfluenceContent({
    contentId: id,
  })
  useEffect(() => {
    if (whatsNewPage?.children?.[0] && !id) {
      router.push(ROUTES.WHATS_NEW_DETAILS.replace(':id', whatsNewPage.children[0].id))
    }
  }, [id, whatsNewPage, router])

  if (isLoading || !id) {
    return (
      <DashboardLayout>
        <Title value="Loading..." />
        <LoadingWhatsNewDetails />
      </DashboardLayout>
    )
  }

  if (isError) {
    return (
      <DashboardLayout>
        <Title value="Error" />
        <div className="row justify-content-center mt-5">
          <div className="col-12 col-lg-10 mt-md-5 text-md-center">
            <ErrorContent error={error} style={{ marginTop: '7.5rem' }}>
              <button
                type="button"
                className="btn btn-link"
                onClick={() => {
                  window.location.reload()
                }}
              >
                <FontAwesomeIcon className="me-2" icon={faRedo} />
                Refresh page
              </button>
            </ErrorContent>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <Title value={currentPage?.title || `What's New`} />
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10">
            <Header
              subtitle="Citrus"
              title="What's New"
              breadcrumbs={[
                { to: ROUTES.DASHBOARD, label: 'Dashboard' },
                { label: currentPage?.title || `What's New` },
              ]}
            />
            <div className="row justify-content-between mt-5">
              <div className="col-xl-3">
                <select
                  className="form-select d-block d-xl-none mb-3"
                  disabled={isLoadingList || isLoading || undefined}
                  value={currentPage?.id}
                  onChange={(evt) => {
                    const { value } = evt.target
                    router.push(ROUTES.WHATS_NEW_DETAILS.replace(':id', value))
                  }}
                >
                  {whatsNewPage?.children?.map((child) => (
                    <option key={child.id} value={child.id}>
                      {child.title}
                    </option>
                  ))}
                </select>
                <ul className="list-group list-group-flush d-none d-xl-block">
                  {(() => {
                    if (isLoadingList) {
                      return (
                        <>
                          {new Array(12)
                            .fill(null)
                            .map((_, i) => `loading-content-row-${i}`)
                            .map((key) => (
                              <div key={key}>
                                <div className="animate mt-3 mb-2" />
                                <div className="animate mb-3" style={{ height: '2rem' }} />
                              </div>
                            ))}
                        </>
                      )
                    }
                    if (isErrorList) {
                      return <ErrorContent size="xs" error={errorList} />
                    }
                    return (
                      <>
                        {whatsNewPage?.children?.map((child) => (
                          <Link
                            className={`list-group-item list-group-item-action${
                              child.id === id ? ' active' : ''
                            }`}
                            to={ROUTES.WHATS_NEW_DETAILS.replace(':id', child.id)}
                            key={`content-${child.id}`}
                          >
                            <h6>
                              {child.title}
                              {!!child.created &&
                                !!lastViewedOn &&
                                new Date(child.created) > lastViewedOn && (
                                  <FontAwesomeIcon
                                    icon={faCircle}
                                    className="text-danger"
                                    transform="shrink-8"
                                  />
                                )}
                            </h6>
                            <TruncatedText lines={2}>{child.summary}</TruncatedText>
                          </Link>
                        ))}
                      </>
                    )
                  })()}
                </ul>
              </div>
              <div className="col-xl-9 mt-xl-0 mt-3">
                <div className="row justify-content-center">
                  <div className="col-10">
                    <h2 className="mb-5">{currentPage?.title}</h2>
                    <div
                      className="whats-new-container"
                      dangerouslySetInnerHTML={{
                        __html: currentPage?.body || '',
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
